import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getActiveClientsWithSOsAndPrograms } from '@/lib/airtable';
import * as Sentry from '@sentry/react';
import { Link, useLocation } from 'react-router-dom';
import { getViewSOLink } from '../../lib/contextAwareRouting';
import { 
  Search,
  Filter,
  Building2,
  AlertTriangle,
  ExternalLink,
  Users,
  Copy
} from 'lucide-react';
import { generatePortalUrl } from '@/lib/partner-portal';
import { toast } from '@/hooks/use-toast';

/**
 * AdminClients - Active clients list interface for the admin dashboard
 * Displays all active clients with their previous/upcoming SOs and current training programs
 * Features search, filtering, and responsive design for mobile/tablet use
 */
const AdminClients = () => {
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterByUpcoming, setFilterByUpcoming] = useState('all');
  const [isReadOnly, setIsReadOnly] = useState(false);


  useEffect(() => {
    loadClientsData();
  }, []);

  /**
   * Load real client data from Airtable
   */
  const loadClientsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await getActiveClientsWithSOsAndPrograms();
      setClients(result.clients || []);

    } catch (error) {
      console.error('Error loading clients data:', error);
      setError('Failed to load client data. Please try again.');
      
      // Check if it's an authentication error for read-only fallback
      // Check various ways HTTP status codes can be exposed in error objects
      const status = error.status || error.statusCode || error.response?.status;
      const statusString = String(status);
      const statusNumber = parseInt(status, 10);
      
      if (statusNumber === 401 || statusNumber === 403 || 
          statusString === '401' || statusString === '403') {
        setIsReadOnly(true);
        setError('Authentication required. Displaying in read-only mode.');
      }
      
      // Report to Sentry
      Sentry.captureException(error);
      
    } finally {
      setLoading(false);
    }
  };

  // Filter clients based on search term and upcoming SO filter
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredClients = clients.filter((client) => {
    const name = (client?.name ?? '').toString();
    const matchesSearch = !normalizedSearch || name.toLowerCase().includes(normalizedSearch);
    
    if (filterByUpcoming === 'with-upcoming') {
      return matchesSearch && !!client?.upcomingSO;
    } else if (filterByUpcoming === 'without-upcoming') {
      return matchesSearch && !client?.upcomingSO;
    }
    
    return matchesSearch;
  });

  // Sort clients alphabetically by name
  const sortedClients = [...filteredClients].sort((a, b) =>
    (a?.name ?? '').localeCompare(b?.name ?? '')
  );

  /**
   * Render current training program information with tooltip and SO link
   */
  const renderProgramInfo = (program) => {
    if (!program) {
      return <span className="text-gray-500 text-sm">None</span>;
    }
    
    // Format dates for tooltip
    const formatDate = (dateString) => {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    };

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link 
              to={getViewSOLink(program.soNumber, { location })}
              className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 hover:underline transition-colors"
            >
              <span className="text-sm font-medium">
                {program.soNumber}
              </span>
              <ExternalLink className="h-3 w-3" />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-2">
              <div className="font-medium">{program.name}</div>
              <div className="text-xs text-gray-300 space-y-1">
                <div>Status: {program.status}</div>
                <div>Start: {formatDate(program.startDate)}</div>
                <div>End: {formatDate(program.endDate)}</div>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  /**
   * Render SO information with link to SO detail view and tooltip
   */
  const renderSOInfo = (so) => {
    if (!so) {
      return <span className="text-gray-500 text-sm">None</span>;
    }

    // Format dates for tooltip
    const formatDate = (dateString) => {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    };

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link 
              to={getViewSOLink(so.soNumber, { location })}
              className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 hover:underline transition-colors"
            >
              <span className="text-sm font-medium">{so.soNumber}</span>
              <ExternalLink className="h-3 w-3" />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-2">
              <div className="font-medium">{so.programName || 'Unknown Program'}</div>
              <div className="text-xs text-gray-300 space-y-1">
                <div>Status: {so.status || 'Unknown'}</div>
                {so.startDate && <div>Start: {formatDate(so.startDate)}</div>}
                {so.endDate && <div>End: {formatDate(so.endDate)}</div>}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  /**
   * Handle copying portal link to clipboard
   */
  const handleCopyPortalLink = async (client) => {
    if (!client.portal_token) {
      toast({
        title: "No Portal Token",
        description: "This client doesn't have a portal token yet. Generate tokens first.",
        variant: "destructive",
      });
      return;
    }

    try {
      const portalUrl = generatePortalUrl(client.portal_token);
      await navigator.clipboard.writeText(portalUrl);
      
      toast({
        title: "Portal Link Copied",
        description: `Portal link for ${client.name} copied to clipboard`,
      });
    } catch (error) {
      toast({
        title: "Copy Failed",
        description: "Failed to copy portal link to clipboard",
        variant: "destructive",
      });
    }
  };

  /**
   * Render portal actions (copy link, open portal)
   */
  const renderPortalActions = (client) => {
    // Check if client has portal token and it's enabled
    const hasPortalAccess = client.portal_token && client.portal_enabled;
    
    if (!hasPortalAccess) {
      return (
        <div className="text-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="secondary" className="text-xs">
                  No Token
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>Generate portal tokens to enable client access</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    }

    const portalUrl = generatePortalUrl(client.portal_token);

    return (
      <div className="flex items-center space-x-2">
        {/* Copy Link Button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopyPortalLink(client)}
                className="h-8 w-8 p-0"
              >
                <Copy className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Copy portal link</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Open Portal Button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(portalUrl, '_blank')}
                className="h-8 w-8 p-0"
              >
                <ExternalLink className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Open portal in new tab</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Status Badge */}
        <Badge variant="success" className="text-xs">
          Active
        </Badge>
      </div>
    );
  };

  /**
   * Render loading skeleton for table rows
   */
  const renderLoadingSkeleton = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        <TableCell>
          <Skeleton className="h-4 w-48" />
        </TableCell>
        <TableCell>
          <div className="space-y-1">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-20" />
          </div>
        </TableCell>
        <TableCell>
          <div className="space-y-1">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-16" />
          </div>
        </TableCell>
        <TableCell>
          <div className="space-y-1">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-20" />
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center space-x-2">
            <Skeleton className="h-6 w-6 rounded" />
            <Skeleton className="h-6 w-6 rounded" />
            <Skeleton className="h-5 w-12 rounded-full" />
          </div>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="text-3xl font-bold tracking-tight text-gray-900">
          Active Clients
        </h2>
        <p className="text-gray-600 mt-2">
          Overview of all active clients with their training history and current training programs.
        </p>
        {isReadOnly && (
          <div className="mt-2 text-sm text-amber-600">
            ⚠️ Read-only mode - authentication required for full access
          </div>
        )}
      </div>


      {/* Clients Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>All Active Clients</CardTitle>
              <CardDescription>
                Complete overview of client training history and current training programs.
              </CardDescription>
            </div>
            <Badge variant="outline" className="self-start sm:self-auto">
              {sortedClients.length} clients
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search and Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 min-h-[44px]"
                  aria-label="Search clients"
                  role="searchbox"
                  aria-controls="clients-table"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <select
                  value={filterByUpcoming}
                  onChange={(e) => setFilterByUpcoming(e.target.value)}
                  className="flex h-11 min-h-[44px] w-full sm:w-auto rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label="Filter clients by upcoming SO"
                >
                  <option value="all">All clients</option>
                  <option value="with-upcoming">With upcoming SO</option>
                  <option value="without-upcoming">Without upcoming SO</option>
                </select>
              </div>
            </div>

            {/* Error State */}
            {error && !isReadOnly && (
              <div className="flex items-center justify-center p-8 text-center">
                <div className="space-y-2">
                  <AlertTriangle className="h-8 w-8 text-red-500 mx-auto" />
                  <p className="text-red-600 text-sm">{error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.reload()}
                    className="min-h-[44px]"
                  >
                    Reload
                  </Button>
                </div>
              </div>
            )}

            {/* Table */}
            {!error || isReadOnly ? (
              <div className="rounded-md border">
                <Table id="clients-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[200px]">Client Name</TableHead>
                      <TableHead className="min-w-[150px]">Last</TableHead>
                      <TableHead className="min-w-[150px]">Current</TableHead>
                      <TableHead className="min-w-[150px]">Next</TableHead>
                      <TableHead className="min-w-[120px]">Portal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      renderLoadingSkeleton()
                    ) : sortedClients.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8">
                          <div className="space-y-2">
                            <Users className="h-8 w-8 text-gray-400 mx-auto" />
                            <p className="text-gray-500 text-sm">
                              {searchTerm || filterByUpcoming !== 'all' 
                                ? 'No clients match your search criteria' 
                                : 'No active clients found'
                              }
                            </p>
                            {(searchTerm || filterByUpcoming !== 'all') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSearchTerm('');
                                  setFilterByUpcoming('all');
                                }}
                                className="min-h-[44px]"
                              >
                                Clear filters
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedClients.map((client) => (
                        <TableRow key={client.id} className="hover:bg-muted/50">
                          <TableCell className="font-medium">
                            <div className="flex items-center space-x-2">
                              <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              {client.portal_token && client.portal_enabled ? (
                                <a
                                  href={generatePortalUrl(client.portal_token)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="truncate text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                                  title={`${client.name} - Click to view portal`}
                                >
                                  {client.name}
                                </a>
                              ) : (
                                <span className="truncate" title={client.name}>
                                  {client.name}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {renderSOInfo(client.previousSO)}
                          </TableCell>
                          <TableCell>
                            {renderProgramInfo(client.currentProgram)}
                          </TableCell>
                          <TableCell>
                            {renderSOInfo(client.upcomingSO)}
                          </TableCell>
                          <TableCell>
                            {renderPortalActions(client)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default AdminClients;